import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

import { projectService } from '../../services/projectService';
import LoginModal from '../administrative-dashboard/components/LoginModal';

const ProjectStatusTracker = () => {
  const { variants, transition } = usePageTransition();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Data State
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadProjectDetails(selectedProject);
    }
  }, [selectedProject]);

  const loadData = async () => {
    try {
      setLoading(true);
      const projectsData = await projectService?.getProjects({ limit: 50 });
      setProjects(projectsData || []);
      
      // Auto-select first project
      if (projectsData?.length > 0) {
        setSelectedProject(projectsData?.[0]?.id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (projectId) => {
    try {
      const [milestonesData, activitiesData] = await Promise.all([
        projectService?.getMilestones(projectId),
        projectService?.getActivities(projectId),
      ]);
      
      setMilestones(milestonesData || []);
      setActivities(activitiesData || []);
    } catch (error) {
      console.error('Error loading project details:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'var(--color-trust)',
      in_progress: 'var(--color-primary)',
      on_hold: 'var(--color-warning)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)',
      pending: 'var(--color-warning)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      planning: 'Planification',
      in_progress: 'En cours',
      on_hold: 'En pause',
      completed: 'Terminé',
      cancelled: 'Annulé',
      pending: 'En attente',
    };
    return labels?.[status] || status;
  };

  const currentProject = projects?.find(p => p?.id === selectedProject);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <motion.div 
        className="flex-1 pt-28 pb-8 md:pb-12"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Suivi de Projet
                </h1>
                <p className="text-muted-foreground">
                  Suivez l'avancement de vos projets en temps réel
                </p>
              </div>
              <Button onClick={() => navigate('/client-portal-dashboard')} variant="outline">
                <Icon name="ArrowLeft" size={20} className="mr-2" />
                Retour au Portail
              </Button>
            </div>

            {/* Project Selector */}
            {projects?.length > 0 && (
              <div className="bg-card rounded-xl p-4 border border-border mb-6">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Sélectionner un projet
                </label>
                <Select
                  options={projects?.map(p => ({
                    value: p?.id,
                    label: `${p?.projectNumber} - ${p?.title}`,
                  }))}
                  value={selectedProject}
                  onChange={setSelectedProject}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {currentProject ? (
            <div className="space-y-6">
              {/* Project Overview Card */}
              <div className="bg-card rounded-xl shadow-premium border border-border p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h2 className="text-xl md:text-2xl font-bold text-foreground">
                        {currentProject?.title}
                      </h2>
                      <span
                        className="px-3 py-1 rounded-full text-sm font-semibold"
                        style={{
                          backgroundColor: `${getStatusColor(currentProject?.status)}15`,
                          color: getStatusColor(currentProject?.status),
                        }}
                      >
                        {getStatusLabel(currentProject?.status)}
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Projet #{currentProject?.projectNumber}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Date de début</p>
                        <p className="font-semibold text-foreground">
                          {new Date(currentProject?.startDate)?.toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Fin prévue</p>
                        <p className="font-semibold text-foreground">
                          {currentProject?.estimatedCompletion
                            ? new Date(currentProject?.estimatedCompletion)?.toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                              })
                            : 'Non définie'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Budget</p>
                        <p className="font-semibold text-foreground">
                          {currentProject?.totalBudget?.toLocaleString()} {currentProject?.currency || 'USD'}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">Progression globale</span>
                        <span className="text-lg font-bold text-primary">
                          {currentProject?.progressPercentage || 0}%
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div
                          className="bg-primary rounded-full h-3 transition-all duration-500"
                          style={{ width: `${currentProject?.progressPercentage || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {currentProject?.description && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-foreground">{currentProject?.description}</p>
                  </div>
                )}
              </div>

              {/* Timeline & Milestones */}
              <div className="bg-card rounded-xl shadow-premium border border-border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Icon name="Calendar" size={24} className="text-primary" />
                  <h3 className="text-xl font-bold text-foreground">Jalons du Projet</h3>
                </div>

                {milestones?.length > 0 ? (
                  <div className="space-y-4">
                    {milestones?.map((milestone, index) => (
                      <div key={milestone?.id} className="relative">
                        {/* Timeline Line */}
                        {index < milestones?.length - 1 && (
                          <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-border" />
                        )}
                        
                        <div className="flex gap-4">
                          {/* Timeline Dot */}
                          <div
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center z-10"
                            style={{
                              backgroundColor: milestone?.status === 'completed'
                                ? 'var(--color-success)'
                                : milestone?.status === 'in_progress' ?'var(--color-primary)' :'var(--color-muted)',
                            }}
                          >
                            {milestone?.status === 'completed' ? (
                              <Icon name="Check" size={16} className="text-white" />
                            ) : (
                              <div className="w-3 h-3 rounded-full bg-white" />
                            )}
                          </div>

                          {/* Milestone Content */}
                          <div className="flex-1 bg-muted/50 rounded-lg p-4 mb-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground mb-1">
                                  {milestone?.title}
                                </h4>
                                {milestone?.description && (
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {milestone?.description}
                                  </p>
                                )}
                                <div className="flex gap-4 text-xs text-muted-foreground">
                                  <span>
                                    Début: {new Date(milestone?.startDate)?.toLocaleDateString('fr-FR')}
                                  </span>
                                  <span>
                                    Fin: {new Date(milestone?.endDate)?.toLocaleDateString('fr-FR')}
                                  </span>
                                </div>
                              </div>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                                style={{
                                  backgroundColor: `${getStatusColor(milestone?.status)}15`,
                                  color: getStatusColor(milestone?.status),
                                }}
                              >
                                {getStatusLabel(milestone?.status)}
                              </span>
                            </div>

                            {/* Milestone Progress */}
                            {milestone?.status === 'in_progress' && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">Progression</span>
                                  <span className="font-semibold text-foreground">
                                    {milestone?.progressPercentage || 0}%
                                  </span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-1.5">
                                  <div
                                    className="bg-primary rounded-full h-1.5 transition-all"
                                    style={{ width: `${milestone?.progressPercentage || 0}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucun jalon défini pour ce projet</p>
                  </div>
                )}
              </div>

              {/* Activity Feed */}
              <div className="bg-card rounded-xl shadow-premium border border-border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Icon name="Activity" size={24} className="text-primary" />
                  <h3 className="text-xl font-bold text-foreground">Activités Récentes</h3>
                </div>

                {activities?.length > 0 ? (
                  <div className="space-y-3">
                    {activities?.slice(0, 10)?.map((activity) => (
                      <div
                        key={activity?.id}
                        className="flex gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                      >
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: 'var(--color-primary-light)' }}
                        >
                          <Icon
                            name={
                              activity?.activityType === 'milestone_completed'
                                ? 'CheckCircle'
                                : activity?.activityType === 'status_change' ?'RefreshCw'
                                : activity?.activityType === 'document_added' ?'FileText' :'Bell'
                            }
                            size={20}
                            className="text-primary"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">
                            {activity?.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity?.createdAt)?.toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucune activité récente</p>
                  </div>
                )}
              </div>

              {/* Contact Support */}
              <div className="bg-primary/10 border border-primary rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <Icon name="MessageCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-2">
                      Questions sur ce projet ?
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Notre équipe projet est à votre disposition pour toute question ou préoccupation.
                    </p>
                    <Button onClick={() => navigate('/contact')} size="sm">
                      <Icon name="MessageCircle" size={16} className="mr-2" />
                      Contacter l'Équipe Projet
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-xl shadow-premium border border-border p-12 text-center">
              <Icon name="Briefcase" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Aucun projet disponible
              </h3>
              <p className="text-muted-foreground mb-6">
                Vous n'avez pas encore de projet en cours. Demandez un devis pour commencer.
              </p>
              <Button onClick={() => navigate('/services-overview')}>
                <Icon name="Plus" size={20} className="mr-2" />
                Demander un Devis
              </Button>
            </div>
          )}
        </div>
      </motion.div>
      <Footer />
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => navigate('/')}
          onLogin={() => setShowLoginModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectStatusTracker;